// Changelog media assets
import headerBarVideo from "@/assets/changelog/header_bar_updates.mp4";
import editEntryImg from "@/assets/changelog/edit_entry_updates.png";
import settingsImg from "@/assets/changelog/settings_updates.png";
import filterByAppImg from "@/assets/changelog/filter_by_application.png";
import supportPageVideo from "@/assets/changelog/support_page.mp4";
import faqPageVideo from "@/assets/changelog/FAQ_page.mp4";

export type ChangelogCategory = "updates" | "bugfixes" | "support";

export interface ChangelogItem {
	category: ChangelogCategory;
	type: "feature" | "improvement" | "bugfix";
	title: string;
	titleIcon?: string;
	description: string;
	details?: string[];
	media?: { src: string; alt: string };
}

export interface ChangelogEntry {
	version: string;
	date: string;
	items: ChangelogItem[];
}

export const changelog: ChangelogEntry[] = [
	{
		version: "1.44.3",
		date: "2026-01-29",
		items: [
			// === UPDATES ===
			{
				category: "updates",
				type: "feature",
				title: "CI/CD PIPELINE UPDATE",
				titleIcon: "panel-top",
				description:
					"Quick access to your most-used actions, always visible at the top of your screen.",
				details: [
					"Date selector arrows let you toggle between days seamlessly",
					"Combine and Delete buttons moved to left side for shorter mouse travel",
					"Duplicate button to easily copy an entry with one click",
					"Hardcoded matter resets every 5 hours, so you won't accidentally bill the wrong case",
					"Hovering over icons shows helpful descriptions",
				],
				media: { src: headerBarVideo, alt: "Header bar demo" },
			},
			{
				category: "updates",
				type: "feature",
				title: "Filter by Application",
				titleIcon: "sliders-horizontal",
				description:
					"Focus on what matters. Toggle which applications appear in your time entries list.",
				details: [
					"Toggle off one or more applications to remove them from your daily view",
					"Time entries don't disappear. To see them again, just toggle back on",
					"Works on both Tracked and Exported pages",
				],
				media: { src: filterByAppImg, alt: "Filter dropdown" },
			},
			{
				category: "updates",
				type: "feature",
				title: "Enhanced Edit Entry",
				titleIcon: "square-pen",
				description:
					"More control over your narratives with smarter editing tools.",
				details: [
					"Abbreviations and shortcuts automatically expand common legal terms. View list in settings, and reach out to us for custom additions",
					"Duplicate button on every Add and Edit entry window for quick copying",
					"Use generate with AI to change existing Hourglass-generated narratives",
				],
				media: { src: editEntryImg, alt: "Edit entry modal" },
			},
			{
				category: "updates",
				type: "feature",
				title: "Expanded Settings",
				titleIcon: "settings",
				description:
					"More customization options to tailor Hourglass to your billing style.",
				details: [
					"Paralegal Mode generates detailed, task-focused narratives for support staff",
					"Abbreviations table to view and use shorthand for common legal terms",
					"Max word count lets you control how long your narratives can be",
				],
				media: { src: settingsImg, alt: "Settings page" },
			},
			// === BUG FIXES ===
			{
				category: "bugfixes",
				type: "bugfix",
				title: "Stability Improvements",
				titleIcon: "bug",
				description:
					"We fixed commonly mentioned issues to make Hourglass more reliable.",
				details: [
					"Email entries now default to activity code A106",
					"Fixed issue where edits required multiple attempts to save",
					'Removed "no sender/recipient identified" from email narratives',
					"Zurich matters now support 0.05-unit increments",
				],
			},
			// === SUPPORT ===
			{
				category: "support",
				type: "feature",
				title: "New Support Page",
				titleIcon: "help-circle",
				description:
					"Get help faster with a dedicated support page. Schedule meetings directly with our team through the integrated calendar.",
				media: { src: supportPageVideo, alt: "Support page walkthrough" },
			},
			{
				category: "support",
				type: "feature",
				title: "Searchable FAQ",
				titleIcon: "help-circle",
				description:
					"Find answers instantly with our comprehensive FAQ section. Browse by category or search for specific topics.",
				media: { src: faqPageVideo, alt: "FAQ page walkthrough" },
			},
		],
	},
];

export const CURRENT_VERSION = changelog[0]?.version || "1.0.0";
